import { useMemo } from "react";

import { LimitedChipItems } from "app/pages/sales-intelligence/common-components/filters-panel";
import { FilterResultContainer } from "app/pages/sales-intelligence/common-components/filters-panel/components/styles";
import { INDUSTRIES_CODE_NAMES, industryCodeDtoMap } from "app/pages/sales-intelligence/domain/industry/types/codes";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";

import { MAX_CHIPS_GROUP_AMOUNT } from "../../../constants";
import type IndustryCodesFilterModel from "../../../models/IndustryCodesFilter/IndustryCodesFilterModel";
import { IndustryCodesFilterValue } from "../../../models/IndustryCodesFilter/IndustryCodesFilterValue";
import industryCodesFilterTrackingService from "../../../services/tracking/industry-codes-filter/trackingService";
import type { IIndustryChipItem } from "../LegacyIndustryFilter/types";
import createIndustryChipItems from "../LegacyIndustryFilter/utils/createIndustryChipItems";

const IndustryCodesFilterResult = ({
    value,
    isExpanded,
    isActionEnabled = true,
    onGetLifecycleStatus,
    onFilterUpdate,
    onFilterValueRemove,
}: IFilterViewBaseProps<IndustryCodesFilterModel>) => {
    const filterValue = useMemo(() => new IndustryCodesFilterValue(value), [value]);

    const chipItems: IIndustryChipItem[] = useMemo(() => {
        const sicCodesItems = createIndustryChipItems(
            filterValue.sicCodes,
            industryCodeDtoMap.sic,
            onGetLifecycleStatus,
        );
        const naicsCodesItems = createIndustryChipItems(
            filterValue.naicsCodes,
            industryCodeDtoMap.naics,
            onGetLifecycleStatus,
        );

        return [...sicCodesItems, ...naicsCodesItems];
    }, [filterValue.sicCodes, filterValue.naicsCodes, onGetLifecycleStatus]);

    if (!chipItems.length) {
        return null;
    }

    const handleClose = (_: string, item: IIndustryChipItem) => {
        const codesKey = item.type === industryCodeDtoMap.sic ? "sicCodes" : "naicsCodes";
        const newValue = filterValue.with({
            [codesKey]: filterValue[codesKey].filter((code) => code !== item.id),
        });

        industryCodesFilterTrackingService.trackingIndustryCodesChange(INDUSTRIES_CODE_NAMES[item.type], "remove");
        onFilterUpdate(newValue);
        onFilterValueRemove?.(newValue);
    };

    return (
        <FilterResultContainer>
            <LimitedChipItems
                items={chipItems}
                onDelete={handleClose}
                maxItems={MAX_CHIPS_GROUP_AMOUNT}
                hideActionButton={!isActionEnabled}
                isTabExpanded={isExpanded}
            />
        </FilterResultContainer>
    );
};

export default IndustryCodesFilterResult;
